import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import WhiteNavbar from "./WhiteNavbar";
import MobileMenu from "./MobileMenu";

const AppHeader: React.FC = () => {
  const [showWhiteNavbar, setShowWhiteNavbar] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const openMobileMenu = useCallback(() => setIsMobileMenuOpen(true), []);
  const closeMobileMenu = useCallback(() => setIsMobileMenuOpen(false), []);

  useEffect(() => {
    const handleScroll = () => {
      // Don't show white navbar on Contact page
      if (location.pathname === '/contact') {
        setShowWhiteNavbar(false);
        return;
      }

      // Find the Banner section
      const bannerSection = document.querySelector('.elementor-section[data-id="1f8a680"]');

      if (bannerSection) {
        const bannerElement = bannerSection as HTMLElement;
        const bannerBottom = bannerElement.offsetTop + bannerElement.offsetHeight;
        const scrollTop = window.scrollY;

        // Show white navbar when scrolling past the banner section
        const shouldShowWhite = scrollTop > bannerBottom;
        setShowWhiteNavbar(shouldShowWhite);
      } else {
        // Fallback: use 100px if banner section not found
        const scrollTop = window.scrollY;
        const shouldShowWhite = scrollTop > 100;
        setShowWhiteNavbar(shouldShowWhite);
      }
    };

    // Initial check
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]);

  // Hover functionality for main header menus
  useEffect(() => {
    const menuSelectors = [
      '.sc_layouts_menu_nav', // Main menu
      '#menu_main' // Fixed header menu
    ];

    const initializeHoverForMenu = (menuRoot: Element) => {
      const items = menuRoot.querySelectorAll('.menu-item-has-children');
      const hoverTimers = new Map<Element, NodeJS.Timeout>();
      const cleanups: Array<() => void> = [];

      // Clear all existing hover states
      const clearAllHovers = () => {
        items.forEach(item => {
          (item as HTMLElement).classList.remove('sfHover');
          const timer = hoverTimers.get(item);
          if (timer) {
            clearTimeout(timer);
            hoverTimers.delete(item);
          }
        });
      };

      // Check if element is within menu bounds
      const isWithinMenuBounds = (element: Element, x: number, y: number): boolean => {
        const rect = element.getBoundingClientRect();
        const padding = 5;
        return x >= (rect.left - padding) &&
          x <= (rect.right + padding) &&
          y >= (rect.top - padding) &&
          y <= (rect.bottom + padding);
      };

      // Check if cursor is over menu item or its submenu (including nested submenus)
      const isOverMenuItem = (menuItem: Element, x: number, y: number): boolean => {
        if (isWithinMenuBounds(menuItem, x, y)) return true;

        // Check all submenus (including nested ones)
        const submenus = Array.from(menuItem.querySelectorAll('.sub-menu'));
        for (const submenu of submenus) {
          if (isWithinMenuBounds(submenu, x, y)) return true;
        }

        return false;
      };

      // Mouse enter handler
      const handleMouseEnter = (menuItem: Element) => {
        const el = menuItem as HTMLElement;

        // Clear any existing timer
        const existingTimer = hoverTimers.get(el);
        if (existingTimer) {
          clearTimeout(existingTimer);
          hoverTimers.delete(el);
        }

        // Close other open menus at the same level (but not nested ones)
        const parentUl = el.parentElement;
        if (parentUl && parentUl.classList.contains('sub-menu')) {
          // Only close siblings at the same level, not parent menus
          parentUl.querySelectorAll(':scope > li.menu-item-has-children.sfHover').forEach(sibling => {
            if (sibling !== el) {
              (sibling as HTMLElement).classList.remove('sfHover');
              const siblingTimer = hoverTimers.get(sibling);
              if (siblingTimer) {
                clearTimeout(siblingTimer);
                hoverTimers.delete(sibling);
              }
            }
          });
        } else if (parentUl && !parentUl.classList.contains('sub-menu')) {
          // For top-level menus, close all other top-level menus
          parentUl.querySelectorAll(':scope > li.menu-item-has-children.sfHover').forEach(sibling => {
            if (sibling !== el) {
              (sibling as HTMLElement).classList.remove('sfHover');
              const siblingTimer = hoverTimers.get(sibling);
              if (siblingTimer) {
                clearTimeout(siblingTimer);
                hoverTimers.delete(sibling);
              }
            }
          });
        }

        el.classList.add('sfHover');
      };

      // Mouse leave handler
      const handleMouseLeave = (menuItem: Element) => {
        const el = menuItem as HTMLElement;
        const timer = setTimeout(() => {
          el.classList.remove('sfHover');
          hoverTimers.delete(el);
        }, 300);
        hoverTimers.set(el, timer);
      };

      // Global mouse move handler for better tracking
      const handleGlobalMouseMove = (e: MouseEvent) => {
        const x = e.clientX;
        const y = e.clientY;

        // Check if mouse is over any menu item or its submenus
        let foundHover = false;
        items.forEach(item => {
          if (isOverMenuItem(item, x, y)) {
            foundHover = true;
            handleMouseEnter(item);
          }
        });

        // If not over any menu item, close all
        if (!foundHover) {
          clearAllHovers();
        }
      };

      // Add event listeners to each menu item
      items.forEach(item => {
        const el = item as HTMLElement;

        // Mouse enter
        const mouseEnterHandler = () => handleMouseEnter(item);
        el.addEventListener('mouseenter', mouseEnterHandler);

        // Mouse leave
        const mouseLeaveHandler = () => handleMouseLeave(item);
        el.addEventListener('mouseleave', mouseLeaveHandler);

        // Also handle all submenus (including nested ones)
        const submenus = Array.from(item.querySelectorAll('.sub-menu'));
        submenus.forEach(submenu => {
          submenu.addEventListener('mouseenter', mouseEnterHandler);
          submenu.addEventListener('mouseleave', mouseLeaveHandler);
        });

        // Cleanup
        cleanups.push(() => {
          el.removeEventListener('mouseenter', mouseEnterHandler);
          el.removeEventListener('mouseleave', mouseLeaveHandler);
          submenus.forEach(submenu => {
            submenu.removeEventListener('mouseenter', mouseEnterHandler);
            submenu.removeEventListener('mouseleave', mouseLeaveHandler);
          });
          const timer = hoverTimers.get(item);
          if (timer) clearTimeout(timer);
        });
      });

      // Global mouse move listener
      document.addEventListener('mousemove', handleGlobalMouseMove, { passive: true });
      cleanups.push(() => document.removeEventListener('mousemove', handleGlobalMouseMove));

      // Close on outside click or submenu item click
      const handleClick = (e: MouseEvent) => {
        const target = e.target as Node | null;
        if (!target) return;

        // If clicking outside the menu, close all
        if (!menuRoot.contains(target)) {
          clearAllHovers();
          return;
        }

        // If clicking on a submenu item (Link), close the submenu
        const submenuItem = (target as Element).closest('.sub-menu a, .sub-menu [href]');
        if (submenuItem) {
          clearAllHovers();
          return;
        }
      };
      document.addEventListener('click', handleClick);
      cleanups.push(() => document.removeEventListener('click', handleClick));

      // Handle parent link clicks
      const anchors = menuRoot.querySelectorAll('a[href="#"], .menu-item-has-children > a.sf-with-ul');
      anchors.forEach(a => {
        const handler = (e: Event) => {
          e.preventDefault();
          const li = (a as HTMLElement).closest('.menu-item-has-children') as HTMLElement | null;
          if (!li) return;

          const isOpen = li.classList.contains('sfHover');
          clearAllHovers();

          if (!isOpen) {
            li.classList.add('sfHover');
          }
        };
        a.addEventListener('click', handler);
        cleanups.push(() => a.removeEventListener('click', handler));
      });

      return () => {
        clearAllHovers();
        cleanups.forEach(fn => fn());
      };
    };

    // Initialize hover for all menu selectors
    const cleanupFunctions: Array<() => void> = [];
    menuSelectors.forEach(selector => {
      const menuRoot = document.querySelector(selector);
      if (menuRoot) {
        const cleanup = initializeHoverForMenu(menuRoot);
        cleanupFunctions.push(cleanup);
      }
    });

    return () => {
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [location.pathname]);

  // Close submenus on every page load and route change
  useEffect(() => {
    const closeAllSubmenus = () => {
      const menuSelectors = [
        '.sc_layouts_menu_nav', // Main menu
        '#menu_main' // Fixed header menu
      ];

      menuSelectors.forEach(selector => {
        const menuRoot = document.querySelector(selector);
        if (!menuRoot) return;

        // Close all submenus
        const items = menuRoot.querySelectorAll('.menu-item-has-children');
        items.forEach(item => {
          (item as HTMLElement).classList.remove('sfHover');
        });
      });
    };

    // Close immediately on component mount
    closeAllSubmenus();

    // Close on every route change
    closeAllSubmenus();
  }, [location.pathname, showWhiteNavbar]);

  // Close submenus on page load/refresh
  useEffect(() => {
    const closeAllSubmenus = () => {
      const menuSelectors = [
        '.sc_layouts_menu_nav', // Main menu
        '#menu_main' // Fixed header menu
      ];

      menuSelectors.forEach(selector => {
        const menuRoot = document.querySelector(selector);
        if (!menuRoot) return;

        // Close all submenus
        const items = menuRoot.querySelectorAll('.menu-item-has-children');
        items.forEach(item => {
          (item as HTMLElement).classList.remove('sfHover');
        });
      });
    };

    // Close on page load
    closeAllSubmenus();

    // Close on window focus (when user comes back to tab)
    const handleFocus = () => closeAllSubmenus();
    window.addEventListener('focus', handleFocus);

    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Add navigation functionality to menu clicks
  useEffect(() => {
    const menuSelectors = [
      '.sc_layouts_menu_nav', // Main menu
      '#menu_main' // Fixed header menu
    ];

    const handleMenuClick = (e: Event) => {
      const target = e.target as Element | null;
      if (!target) return;

      // Check if clicked element is a link
      const link = target.closest('a');
      if (link) {
        const href = link.getAttribute('href');

        // Only handle internal links (not external or # links)
        if (href && href !== '#' && !href.startsWith('http') && !href.startsWith('mailto:') && !href.startsWith('tel:')) {
          // Prevent default React Router behavior
          e.preventDefault();
          e.stopPropagation();

          // Close all submenus first
          menuSelectors.forEach(selector => {
            const menuRoot = document.querySelector(selector);
            if (menuRoot) {
              const items = menuRoot.querySelectorAll('.menu-item-has-children');
              items.forEach(item => {
                (item as HTMLElement).classList.remove('sfHover');
              });
            }
          });

          // Small delay to ensure submenus close before navigation
          setTimeout(() => {
            navigate(href);
          }, 50);
        }
      }
    };

    menuSelectors.forEach(selector => {
      const menuRoot = document.querySelector(selector);
      if (menuRoot) {
        menuRoot.addEventListener('click', handleMenuClick);
      }
    });

    return () => {
      menuSelectors.forEach(selector => {
        const menuRoot = document.querySelector(selector);
        if (menuRoot) {
          menuRoot.removeEventListener('click', handleMenuClick);
        }
      });
    };
  }, [navigate]);

  // Active state management for all main menu items and their submenus
  useEffect(() => {
    const menuSelectors = [
      '.sc_layouts_menu_nav', // Main menu
      '#menu_main' // Fixed header menu
    ];

    // Close all submenus immediately when route changes
    const closeAllSubmenus = () => {
      menuSelectors.forEach(selector => {
        const menuRoot = document.querySelector(selector);
        if (!menuRoot) return;

        const items = menuRoot.querySelectorAll('.menu-item-has-children');
        items.forEach(item => {
          (item as HTMLElement).classList.remove('sfHover');
        });
      });
    };

    // Close submenus immediately on route change
    closeAllSubmenus();

    const updateActiveStates = () => {
      menuSelectors.forEach(selector => {
        const root = document.querySelector(selector);
        if (!root) {
          return;
        }

        // Clear all existing active states
        root.querySelectorAll('.current-menu-item, .current_page_item, .current-menu-ancestor, .current-menu-parent')
          .forEach(el => el.classList.remove('current-menu-item', 'current_page_item', 'current-menu-ancestor', 'current-menu-parent'));

        // Find all links in the menu
        const anchors = Array.from(root.querySelectorAll('a[href]')) as HTMLElement[];
        const match = anchors.find(a => {
          const href = (a as HTMLAnchorElement).getAttribute('href') || '';
          // Special handling for root path
          if (location.pathname === '/' && (href === '/' || href === '')) {
            return true;
          }

          try {
            const url = new URL(href, window.location.origin);
            const matches = url.pathname === location.pathname;
            // if (matches) {
            //   console.log('Header: Matched pathname:', url.pathname);
            // }
            return matches;
          } catch {
            const matches = href === location.pathname;
            // if (matches) {
            //   console.log('Header: Matched direct href:', href);
            // }
            return matches;
          }
        });

        if (match) {
          let li: HTMLElement | null = match.closest('li');
          if (li) {
            li.classList.add('current-menu-item', 'current_page_item');
            let parent = li.parentElement;
            while (parent) {
              const pli = parent.closest('li');
              if (pli) {
                pli.classList.add('current-menu-ancestor', 'current-menu-parent');
                parent = pli.parentElement as HTMLElement | null;
              } else {
                break;
              }
            }
          }
        } else {
          console.error('Header: No match found for', location.pathname, 'in', selector);
        }
      });
    };
    updateActiveStates();
  }, [location.pathname]);

  useEffect(() => {
    const forceHomeActive = () => {
      if (location.pathname === '/') {
        const homeItems = document.querySelectorAll('.menu-item-home, .menu-item-16827');
        homeItems.forEach(item => {
          (item as HTMLElement).classList.add('current-menu-item', 'current_page_item');
          let parent = item.parentElement;
          while (parent) {
            const pli = parent.closest('li');
            if (pli) {
              pli.classList.add('current-menu-ancestor', 'current-menu-parent');
              parent = pli.parentElement as HTMLElement | null;
            } else {
              break;
            }
          }
        });
      }
    };

    forceHomeActive();
    setTimeout(forceHomeActive, 50);
    setTimeout(forceHomeActive, 100);
    setTimeout(forceHomeActive, 300);
    setTimeout(forceHomeActive, 500);
    setTimeout(forceHomeActive, 1000);
  }, [location.pathname]);

  useEffect(() => {
    const handleWindowLoad = () => {
      if (location.pathname === '/') {
        const homeItems = document.querySelectorAll('.menu-item-home, .menu-item-16827');
        homeItems.forEach(item => {
          (item as HTMLElement).classList.add('current-menu-item', 'current_page_item');
        });
      }
    };

    // Run on window load
    if (document.readyState === 'complete') {
      handleWindowLoad();
    } else {
      window.addEventListener('load', handleWindowLoad);
    }

    // Also run when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', handleWindowLoad);
    } else {
      handleWindowLoad();
    }

    return () => {
      window.removeEventListener('load', handleWindowLoad);
      document.removeEventListener('DOMContentLoaded', handleWindowLoad);
    };
  }, [location.pathname]);

  return (
    <>
      <header
        className="top_panel top_panel_custom top_panel_custom_19489 top_panel_custom_header-main-sticky-printo without_bg_image">
        <div data-elementor-type="cpt_layouts" data-elementor-id="19489" className="elementor elementor-19489">
          <section
            className="elementor-section elementor-top-section elementor-element elementor-element-a86db5a elementor-section-full_width elementor-section-content-middle sc_layouts_row sc_layouts_row_type_compact sc_layouts_hide_on_tablet sc_layouts_hide_on_mobile elementor-section-height-default elementor-section-height-default sc_fly_static trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
            data-id="a86db5a" data-element_type="section">
            <div className="elementor-container elementor-column-gap-extended">
              <div className="elementor-column elementor-col-50 elementor-top-column elementor-element elementor-element-c92e649 sc_layouts_column_align_left sc_layouts_column sc_content_align_inherit sc_layouts_column_icons_position_left sc_fly_static trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
                data-id="c92e649" data-element_type="column">
                <div className="elementor-widget-wrap elementor-element-populated">
                  <div style={{ marginRight: '0px' }} className="sc_layouts_item elementor-element elementor-element-cd0074c sc_fly_static elementor-widget elementor-widget-trx_sc_layouts_logo trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
                    data-id="cd0074c" data-element_type="widget"
                    data-widget_type="trx_sc_layouts_logo.default">
                    <div className="elementor-widget-container">
                      <Link to="/"
                        className="sc_layouts_logo sc_layouts_logo_default trx_addons_inline_1884545146"
                        style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <img
                          loading="lazy"
                          src="/favicon-modli.png"
                          alt="Modli 3D Icon"
                          width="35"
                          height="35"
                          style={{ display: 'block', height: '3em' }} />
                        <span className="modli-logo-text" style={{
                          fontSize: '23px',
                          fontWeight: '800',
                          color: '#1c212a',
                          letterSpacing: '0.5px',
                          fontFamily: "'Red Hat Display', sans-serif",
                          whiteSpace: 'nowrap'
                        }}>MODLI 3D</span>
                      </Link>
                    </div>
                  </div>
                  <div className="sc_layouts_item elementor-element elementor-element-b5cfee9 sc_layouts_hide_on_wide sc_layouts_hide_on_desktop sc_layouts_hide_on_notebook sc_layouts_hide_on_laptop sc_fly_static elementor-widget elementor-widget-trx_sc_layouts_menu trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
                    data-id="b5cfee9" data-element_type="widget"
                    data-widget_type="trx_sc_layouts_menu.default">
                    <div className="elementor-widget-container">
                      <div className="sc_layouts_iconed_text sc_layouts_menu_mobile_button_burger sc_layouts_menu_mobile_button without_menu">
                        <a
                          className="sc_layouts_item_link sc_layouts_iconed_text_link inited_click"
                          href="/#"
                          role="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openMobileMenu();
                          }}
                        >
                          <span className="sc_layouts_item_icon sc_layouts_iconed_text_icon trx_addons_icon-menu"></span>
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="sc_layouts_item elementor-element elementor-element-e322593 sc_layouts_hide_on_tablet sc_layouts_hide_on_mobile sc_fly_static elementor-widget elementor-widget-trx_sc_layouts_menu trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
                    data-id="e322593" data-element_type="widget"
                    data-widget_type="trx_sc_layouts_menu.default">
                    <div className="elementor-widget-container">
                      <nav className="sc_layouts_menu sc_layouts_menu_default sc_layouts_menu_dir_horizontal menu_hover_zoom_line hide_on_mobile inited inited_kbd"
                        data-animation-in="fadeIn" data-animation-out="fadeOut">
                        <ul id="sc_layouts_menu_699367679"
                          className="sc_layouts_menu_nav inited_cache inited sf-js-enabled sf-arrows"
                          style={{ touchAction: "pan-y" }}>
                          <li className="menu-item current-menu-item">
                            <Link to="/">
                              <span>Home</span>
                            </Link>
                          </li>
                          <li id="menu-item-17651"
                            className="menu-item menu-item-type-custom menu-item-object-custom menu-item-has-children menu-item-17651">
                            <Link to="/about-us"><span>About Us</span></Link>
                          </li>
                          {/* <li id="menu-item-18341"
                          className="menu-item menu-item-type-custom menu-item-object-custom menu-item-has-children menu-item-18341">
                          <button type="button" className="sf-with-ul"><span>Portfolio</span></button>
                          <ul className="sub-menu">
                            <li id="menu-item-18344"
                              className="menu-item menu-item-type-post_type menu-item-object-page menu-item-18344">
                              <Link to="/portfolio-list"><span>Portfolio List</span></Link>
                            </li>
                            <li id="menu-item-18353"
                              className="menu-item menu-item-type-post_type menu-item-object-page menu-item-18353">
                              <Link to="/portfolio-grid"><span>Portfolio Grid</span></Link>
                            </li>
                            <li id="menu-item-18362"
                              className="menu-item menu-item-type-post_type menu-item-object-page menu-item-18362">
                              <Link to="/portfolio-masonry"><span>Portfolio Masonry</span></Link>
                            </li>
                            <li id="menu-item-18368"
                              className="menu-item menu-item-type-post_type menu-item-object-page menu-item-18368">
                              <Link to="/portfolio-fullwidth"><span>Portfolio Fullwidth</span></Link>
                            </li>
                            <li id="menu-item-18380"
                              className="menu-item menu-item-type-post_type menu-item-object-page menu-item-18380">
                              <Link to="/portfolio-horizontal"><span>Portfolio Horizontal</span></Link>
                            </li>
                            <li id="menu-item-18404"
                              className="menu-item menu-item-type-custom menu-item-object-custom menu-item-has-children menu-item-18404">
                              <button type="button" className="sf-with-ul"><span>Single Project</span></button>
                              <ul className="sub-menu">
                                <li id="menu-item-18405"
                                  className="menu-item menu-item-type-post_type menu-item-object-cpt_portfolio menu-item-18405">
                                  <Link to="/portfolio/single-project/right-sidebar"><span>Right Sidebar</span></Link>
                                </li>
                                <li id="menu-item-20691"
                                  className="menu-item menu-item-type-post_type menu-item-object-cpt_portfolio menu-item-20691">
                                  <Link to="/portfolio/single-project/left-sidebar"><span>Left Sidebar</span></Link>
                                </li>
                                <li id="menu-item-20692"
                                  className="menu-item menu-item-type-post_type menu-item-object-cpt_portfolio menu-item-20692">
                                  <Link to="/portfolio/single-project/gallery"><span>Gallery</span></Link>
                                </li>
                                <li id="menu-item-21905"
                                  className="menu-item menu-item-type-post_type menu-item-object-cpt_portfolio menu-item-21905">
                                  <Link to="/portfolio/single-project/background-video"><span>Background Video</span></Link>
                                </li>
                              </ul>
                            </li> 
                          </ul>
                        </li> */}
                          <li id="mobile-menu-item-16830"
                            className="menu-item menu-item-type-post_type menu-item-object-page mobile-menu-item-16830">
                            <Link to="/shop"><span>Shop</span></Link>
                          </li>
                          <li id="mobile-menu-item-16830"
                            className="menu-item menu-item-type-post_type menu-item-object-page mobile-menu-item-16830">
                            <Link to="/contact"><span>Contact Us</span></Link>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          {/* Fixed Header Section */}
          <div className="sc_layouts_row_unfixed_placeholder">
            <div className="sc_layouts_row_fixed_marker_on"></div>
          </div>
          <section data-fixed-row-delay="0.75"
            className="elementor-section elementor-top-section elementor-element elementor-element-87b43d4 elementor-section-full_width elementor-section-content-middle sc_layouts_row sc_layouts_row_type_compact sc_layouts_hide_on_tablet sc_layouts_hide_on_mobile sc_layouts_row_fixed sc_layouts_row_fixed_always sc_layouts_row_delay_fixed sc_layouts_row_hide_unfixed elementor-section-height-default elementor-section-height-default sc_fly_static trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
            data-id="87b43d4" data-element_type="section">
            <div className="elementor-container elementor-column-gap-extended">
              <div className="elementor-column elementor-col-50 elementor-top-column elementor-element elementor-element-d230f73 sc_layouts_column_align_left sc_layouts_column sc_content_align_inherit sc_layouts_column_icons_position_left sc_fly_static trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
                data-id="d230f73" data-element_type="column">
                <div className="elementor-widget-wrap elementor-element-populated">
                  <div className="sc_layouts_item elementor-element elementor-element-014f618 sc_fly_static elementor-widget elementor-widget-trx_sc_layouts_logo trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
                    data-id="014f618" data-element_type="widget"
                    data-widget_type="trx_sc_layouts_logo.default">
                    <div className="elementor-widget-container">
                      <Link to="/"
                        className="sc_layouts_logo sc_layouts_logo_default trx_addons_inline_1884545146"
                        style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <img
                          loading="lazy"
                          src="/favicon-modli.png"
                          alt="Modli 3D Icon"
                          width="35"
                          height="35"
                          style={{ display: 'block', height: '3em' }} />
                        <span style={{
                          fontSize: '23px',
                          fontWeight: '800',
                          color: '#1c212a',
                          letterSpacing: '0.5px',
                          fontFamily: "'Red Hat Display', sans-serif",
                          whiteSpace: 'nowrap'
                        }}>MODLI 3D</span>
                      </Link>
                    </div>
                  </div>
                  <div className="sc_layouts_item elementor-element elementor-element-b5cfee9-fixed sc_layouts_hide_on_wide sc_layouts_hide_on_desktop sc_layouts_hide_on_notebook sc_layouts_hide_on_laptop sc_fly_static elementor-widget elementor-widget-trx_sc_layouts_menu trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
                    data-id="b5cfee9-fixed" data-element_type="widget"
                    data-widget_type="trx_sc_layouts_menu.default">
                    <div className="elementor-widget-container">
                      <div className="sc_layouts_iconed_text sc_layouts_menu_mobile_button_burger sc_layouts_menu_mobile_button without_menu">
                        <a
                          className="sc_layouts_item_link sc_layouts_iconed_text_link inited_click"
                          href="/#"
                          role="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openMobileMenu();
                          }}
                        >
                          <span className="sc_layouts_item_icon sc_layouts_iconed_text_icon trx_addons_icon-menu"></span>
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="sc_layouts_item elementor-element elementor-element-8d93fe0 sc_layouts_hide_on_tablet sc_layouts_hide_on_mobile sc_fly_static elementor-widget elementor-widget-trx_sc_layouts_menu trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
                    data-id="8d93fe0" data-element_type="widget"
                    data-widget_type="trx_sc_layouts_menu.default">
                    <div className="elementor-widget-container">
                      <nav className="sc_layouts_menu sc_layouts_menu_default sc_layouts_menu_dir_horizontal menu_hover_zoom_line inited inited_kbd"
                        data-animation-in="fadeIn" data-animation-out="fadeOut">
                        <ul id="menu_main" className="sc_layouts_menu_nav menu_main_nav inited_cache inited sf-js-enabled sf-arrows"
                          style={{ touchAction: "pan-y" }}>
                          <li className="menu-item menu-item-type-custom menu-item-object-custom current-menu-ancestor current-menu-parent menu-item-has-children menu-item-18409">
                            <button type="button" className="sf-with-ul"><span>Home</span></button>
                            <ul className="sub-menu">
                              <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-home current-menu-item page_item page-item-5002 current_page_item menu-item-16827">
                                <Link to="/" aria-current="page"><span>3D Printing</span></Link>
                              </li>
                              <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-19068">
                                <Link to="/printing-services"><span>Printing Services</span></Link>
                              </li>
                              <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-18892">
                                <Link to="/blogs-and-tutorial"><span>Blogs &amp; Tutorials</span></Link>
                              </li>
                              <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-blogsandartical">
                                <Link to="/blogs-and-artical"><span>Blogs &amp; Artical</span></Link>
                              </li>
                              <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-18408">
                                <Link to="/online-store"><span>Online Store</span></Link>
                              </li>
                              <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-19814">
                                <Link to="/stl-files-store"><span>STL Files Store</span></Link>
                              </li>
                            </ul>
                          </li>
                          <li className="menu-item menu-item-type-custom menu-item-object-custom menu-item-has-children menu-item-17651">
                            <button type="button" className="sf-with-ul"><span>Pages</span></button>
                            <ul className="sub-menu">
                              <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-18105">
                                <Link to="/about-us"><span>About Us</span></Link>
                              </li>
                              <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-16831">
                                <Link to="/our-services"><span>Our Services</span></Link>
                              </li>
                              <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-18072">
                                <Link to="/our-team"><span>Our Team</span></Link>
                              </li>
                              <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-17831">
                                <Link to="/faq"><span>FAQs</span></Link>
                              </li>
                              <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-17933">
                                <Link to="/pricing"><span>Pricing</span></Link>
                              </li>
                              <li className="menu-item menu-item-type-custom menu-item-object-custom menu-item-has-children menu-item-18311">
                                <button type="button" className="sf-with-ul"><span>Blog</span></button>
                                <ul className="sub-menu">
                                  <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-18312">
                                    <Link to="/blog-standard"><span>Blog Standard</span></Link>
                                  </li>
                                </ul>
                              </li>
                            </ul>
                          </li>
                          <li className="menu-item menu-item-type-custom menu-item-object-custom menu-item-has-children menu-item-18341">
                            <button type="button" className="sf-with-ul"><span>Portfolio</span></button>
                            <ul className="sub-menu">
                              <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-18344">
                                <Link to="/portfolio-list"><span>Portfolio List</span></Link>
                              </li>
                              <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-18353">
                                <Link to="/portfolio-grid"><span>Portfolio Grid</span></Link>
                              </li>
                            </ul>
                          </li>
                          <li className="menu-item menu-item-type-custom menu-item-object-custom menu-item-has-children menu-item-18008">
                            <button type="button" className="sf-with-ul"><span>Shop</span></button>
                            <ul className="sub-menu">
                              <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-16832">
                                <Link to="/shop"><span>Product List</span></Link>
                              </li>
                              <li className="menu-item menu-item-type-post_type menu-item-object-product menu-item-18013">
                                <Link to="/product-single"><span>Product Single</span></Link>
                              </li>
                              <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-18012">
                                <Link to="/cart"><span>Cart</span></Link>
                              </li>
                              <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-18011">
                                <Link to="/checkout"><span>Checkout</span></Link>
                              </li>
                              <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-18009">
                                <Link to="/wishlist-page"><span>Wishlist Page</span></Link>
                              </li>
                            </ul>
                          </li>
                          <li id="menu-item-contact"
                            className="menu-item menu-item-type-post_type menu-item-object-page menu-item-contact">
                            <Link to="/contact"><span>Contact Us</span></Link>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          {/* Mobile Header Section */}
          <div className="sc_layouts_row_fixed_placeholder" style={{ backgroundColor: "rgba(0, 0, 0, 0)" }}>
            <div className="sc_layouts_row_fixed_marker_off"></div>
          </div>
          <section className="elementor-section elementor-top-section elementor-element elementor-element-82a5435 elementor-section-content-middle sc_layouts_row sc_layouts_row_type_compact sc_layouts_hide_on_wide sc_layouts_hide_on_desktop sc_layouts_hide_on_notebook elementor-section-boxed elementor-section-height-default elementor-section-height-default sc_fly_static trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
            data-id="82a5435" data-element_type="section">
            <div className="elementor-container elementor-column-gap-extended">
              <div className="elementor-column elementor-col-50 elementor-top-column elementor-element elementor-element-4cd8cf5 sc_layouts_column_align_left sc_layouts_column sc_content_align_inherit sc_layouts_column_icons_position_left sc_fly_static trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
                data-id="4cd8cf5" data-element_type="column">
                <div className="elementor-widget-wrap elementor-element-populated">
                  <div className="sc_layouts_item elementor-element elementor-element-7cf7688 sc_fly_static elementor-widget elementor-widget-trx_sc_layouts_logo trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
                    data-id="7cf7688" data-element_type="widget"
                    data-widget_type="trx_sc_layouts_logo.default">
                    <div className="elementor-widget-container">
                      <Link to="/"
                        className="sc_layouts_logo sc_layouts_logo_default trx_addons_inline_1884545146"
                        style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <img
                          loading="lazy"
                          src="/favicon-modli.png"
                          alt="Modli 3D Icon"
                          width="35"
                          height="35"
                          style={{ display: 'block', height: '3em' }} />
                        <span style={{
                          fontSize: '23px',
                          fontWeight: '800',
                          color: '#1c212a',
                          letterSpacing: '0.5px',
                          fontFamily: "'Red Hat Display', sans-serif",
                          whiteSpace: 'nowrap'
                        }}>MODLI 3D</span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
              <div className="elementor-column elementor-col-50 elementor-top-column elementor-element elementor-element-814d065 sc_layouts_column_align_right sc_layouts_column sc_content_align_inherit sc_layouts_column_icons_position_left sc_fly_static trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
                data-id="814d065" data-element_type="column">
                <div className="elementor-widget-wrap elementor-element-populated">
                  <div className="sc_layouts_item elementor-element elementor-element-581885d sc_fly_static elementor-widget elementor-widget-trx_sc_layouts_menu trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited"
                    data-id="581885d" data-element_type="widget"
                    data-widget_type="trx_sc_layouts_menu.default">
                    <div className="elementor-widget-container">
                      <div className="sc_layouts_iconed_text sc_layouts_menu_mobile_button_burger sc_layouts_menu_mobile_button">
                        <a
                          className="sc_layouts_item_link sc_layouts_iconed_text_link inited_click"
                          href="/#"
                          role="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openMobileMenu();
                          }}
                        >
                          <span className="sc_layouts_item_icon sc_layouts_iconed_text_icon trx_addons_icon-menu"></span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </header>
      <MobileMenu isOpen={isMobileMenuOpen} onClose={closeMobileMenu} />
      <WhiteNavbar show={showWhiteNavbar} onMobileMenuOpen={openMobileMenu} />
    </>
  );
};

export default AppHeader;